import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import { AUTOFILL_MODES, AutofillMode, AutofillResult, autofillAnswers, autofillErrorMessage } from '../../services/answerAutofill';

const COPY: Record<AutofillMode, { title: string, summary: string }> = {
  [AUTOFILL_MODES.QUESTIONNAIRE]: {
    title: 'Autofill Questionnaire Answers',
    summary: 'Answers every question currently in scope as Not Applicable, leaving the last question for you to answer by hand.',
  },
  [AUTOFILL_MODES.ASSESSOR]: {
    title: 'Autofill Assessor Answers',
    summary: 'Approves every question the vendor answered as Fully Implemented, leaving the last one for you to assess by hand.',
  },
};

export default function AutofillAnswersModal({
  mode,
  workflowId,
  isOpen,
  handleClose,
}: {
  mode: AutofillMode,
  workflowId: number,
  isOpen: boolean,
  handleClose: () => void,
}) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AutofillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setRunning(true);
    setError(null);
    try {
      setResult(await autofillAnswers(mode, workflowId));
    } catch (e) {
      setError(autofillErrorMessage(e));
      setRunning(false);
    }
  };

  // A full reload, not a refetch: the survey page recomputes status on mount.
  const handleReload = () => window.location.reload();

  return (
    <StandardDialog
      title={COPY[mode].title}
      isOpen={isOpen}
      handleClose={running ? () => {} : handleClose}
      cantClose={running}
      maxWidth="sm"
    >
      {!result && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>{COPY[mode].summary}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This is an internal testing tool. It does not submit anything, and it cannot be undone.
            Questions you have already answered are left untouched.
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      )}

      {result && (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            {`Answered ${result.answeredCount} question${result.answeredCount === 1 ? '' : 's'}.`}
          </Alert>
          {result.leftUnanswered.length > 0 && (
            <Typography variant="body2">
              {`Left for you: ${result.leftUnanswered.map(question => question.shortCode).join(', ')}`}
            </Typography>
          )}
          {result.skipped.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">Could not answer:</Typography>
              <List dense>
                {result.skipped.map(question => (
                  <ListItem key={question.shortCode}>
                    <ListItemText primary={`${question.shortCode} — ${question.reason}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            Reload the page to see the updated questionnaire.
          </Typography>
        </Box>
      )}

      <StandardDialogActions>
        {!result && <Button onClick={handleClose} disabled={running}>Cancel</Button>}
        {!result && (
          <Button variant="contained" onClick={handleConfirm} disabled={running}>
            {running ? <CircularProgress size={22} /> : 'Confirm'}
          </Button>
        )}
        {result && <Button variant="contained" onClick={handleReload}>Reload</Button>}
      </StandardDialogActions>
    </StandardDialog>
  );
}
